import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Cell,
} from "recharts";
import type { ScoreDistributionBucket } from "@/hooks/dashboard/useDashboardKPIs";
import { KPITooltip } from "./KPITooltip";

interface ScoreComparisonChartProps {
	scoreVsOutcome: {
		hired: { avg: number; count: number };
		interview: { avg: number; count: number };
		closed: { avg: number; count: number };
	};
	distribution: ScoreDistributionBucket[];
	tooltip?: string;
}

export function ScoreComparisonChart({ scoreVsOutcome, distribution, tooltip }: ScoreComparisonChartProps) {
	const { t } = useTranslation();

	const outcomeData = [
		{
			label: t("dashboard.kpi.stages.hired"),
			score: scoreVsOutcome.hired.avg,
			count: scoreVsOutcome.hired.count,
			fill: "#22c55e",
		},
		{
			label: t("dashboard.kpi.stages.interview"),
			score: scoreVsOutcome.interview.avg,
			count: scoreVsOutcome.interview.count,
			fill: "#14b8a6",
		},
		{
			label: t("dashboard.kpi.stages.closed"),
			score: scoreVsOutcome.closed.avg,
			count: scoreVsOutcome.closed.count,
			fill: "#ef4444",
		},
	];

	const hasOutcomeData = scoreVsOutcome.hired.count > 0 || scoreVsOutcome.interview.count > 0 || scoreVsOutcome.closed.count > 0;
	const hasDistribution = distribution.some(b => b.count > 0);

	return (
		<Card>
			<CardHeader className={!hasOutcomeData && !hasDistribution ? "pb-2" : undefined}>
				<div className="flex items-center gap-2">
					<CardTitle className="text-lg">{t("dashboard.kpi.aiScoreOutcome")}</CardTitle>
					{tooltip && <KPITooltip text={tooltip} />}
				</div>
			</CardHeader>
			<CardContent>
				{!hasOutcomeData && !hasDistribution ? (
					<div className="text-muted-foreground text-sm py-2">
						{t("dashboard.kpi.noData")}
					</div>
				) : (
					<div className="space-y-6">
						{hasOutcomeData && (
							<div>
								<h4 className="text-sm font-medium mb-3 text-muted-foreground">
									{t("dashboard.kpi.avgScoreByOutcome")}
								</h4>
								<div className="grid grid-cols-3 gap-4">
									{outcomeData.map((d, i) => (
										<div key={i} className="text-center p-4 rounded-sm bg-muted/50">
											<div className="text-3xl font-semibold" style={{ color: d.fill }}>
												{d.score}
											</div>
											<div className="text-sm text-muted-foreground mt-1">{d.label}</div>
											<div className="text-xs text-muted-foreground">
												({d.count} {t("dashboard.kpi.matches")})
											</div>
										</div>
									))}
								</div>
							</div>
						)}

						{hasDistribution && (
							<div>
								<h4 className="text-sm font-medium mb-3 text-muted-foreground">
									{t("dashboard.kpi.scoreDistribution")}
								</h4>
								<div className="h-[180px]">
									<ResponsiveContainer width="100%" height="100%">
										<BarChart data={distribution} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
											<CartesianGrid strokeDasharray="3 3" opacity={0.1} />
											<XAxis dataKey="range" tick={{ fontSize: 11 }} />
											<YAxis />
											<Tooltip
												contentStyle={{
													backgroundColor: "rgba(255, 255, 255, 0.95)",
													borderRadius: "0.5rem",
													border: "none",
													boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
												}}
											/>
											<Bar dataKey="count" radius={[4, 4, 0, 0]}>
												{distribution.map((_, index) => (
													<Cell
														key={index}
														fill={["#ef4444", "#f97316", "#eab308", "#22c55e", "#10b981"][index]}
													/>
												))}
											</Bar>
										</BarChart>
									</ResponsiveContainer>
								</div>
							</div>
						)}
					</div>
				)}
			</CardContent>
		</Card>
	);
}
